import { CollapseCustom, CollapsePanelCustom, DataTable, Flex, Icon, Number, Text, Title } from "components/common"
import { TitleDescription } from "components/custom"
import { useTranslation } from "react-i18next"
import { convertToSpecificAreaUnit, getAreaUnit, joinBytItems, localisedValue } from "utils"
import theme from "utils/themeVars"
import styles from "../index.module.less"
import { Separator } from "@empglabs/backoffice-ui-kit"
import { useSelector } from "react-redux"

const getIconComponent = iconType => {
  const iconMap = {
    Commercial: "CiShop",
    Homes: "CiHome",
  }

  return (
    <Icon
      style={{ color: theme["primary-color"] }}
      hasBackground={true}
      size={"1.4em"}
      iconContainerStyle={{ borderRadius: "8px", "--icon-bg-color": theme["primary-light"] }}
      icon={iconMap[iconType] || "BiHomeAlt"}
    />
  )
}

const PricingSection = ({ project }) => {
  const { t } = useTranslation()
  const userPlatformKey = useSelector(state => state?.app?.user?.info?.AgencyConfiguration?.Platform?.platformKey)

  if (!project?.pricingAndAvailability?.length) return null

  const pricingColumns = section => [
    ...(section?.type === "beds"
      ? [
        {
          title: t("Bed(s)"),
          dataIndex: "bedCount",
          key: "bedCount",
          className: "tableStartSpaceExtra",
          width: 70
        },
      ]
      : []),

    {
      title: t("Area"),
      dataIndex: "areaRange",
      key: "areaRange",
      width: 180,
      className: "tableStartSpaceExtra",
    },
    ,
    {
      title: t("Price Range"),
      dataIndex: "priceRange",
      key: "priceRange",
    },
    {
      title: t("Units"),
      dataIndex: "availableUnits",
      key: "availableUnits",
    },
  ]

  const tableData = section =>
    section?.categories?.map((detail, index) => {
      return {
        bedCount: <Number value={detail?.beds} currency={false} />,
        areaRange: (
          <>
            <Number
              digitAfterFraction={2}
              value={convertToSpecificAreaUnit(
                detail?.areaRange?.min,
                userPlatformKey === "bayut_ksa" ? "sq_m" : "sq_f"
              )}
            />
            -{" "}
            <Number
              digitAfterFraction={2}
              value={convertToSpecificAreaUnit(
                detail?.areaRange?.max,
                userPlatformKey === "bayut_ksa" ? "sq_m" : "sq_f"
              )}
              unit={getAreaUnit(userPlatformKey)}
            />
          </>
        ),
        priceRange: (
          <>
            <Number isStringOnly={true} isCurrency={true} value={detail?.priceRange?.min} tooltip /> -{" "}
            <Number isStringOnly={true} isCurrency={false} value={detail?.priceRange?.max} tooltip />
          </>
        ),
        availableUnits: (
          <Text>
            {detail?.availableUnits} {t("units")}
          </Text>
        ),
        key: index,
      }
    })

  const renderHeader = section => {
    const title = localisedValue({ object: section, key: "displayName" })

    return (
      <Flex align="center" gap={"12px"}>
        {getIconComponent(section?.unitType)}
        <div>
          <TitleDescription
            title={title}
            description={
              <Flex gap="3px">
                {joinBytItems(
                  [
                    <Text className="fw-medium fs12" style={{ color: theme["gray900"] }}>
                      <Number isStringOnly={true} isCurrency={true} value={section?.priceRange?.min} tooltip /> -{" "}
                      <Number isStringOnly={true} isCurrency={false} value={section?.priceRange?.max} tooltip />
                    </Text>,
                    <Text className="fw-medium fs12" style={{ color: theme["gray900"] }}>
                      {`${section?.totalAvailableUnits} Units`}
                    </Text>,
                  ],
                  <Separator />
                )}
              </Flex>
            }
            gapSpace={2}
            titleClassName="semiBold mbe-0"
          />
        </div>
      </Flex>
    )
  }
  if (project?.pricingAndAvailability?.length === 0) return null
  return (
    <Flex vertical gap={12} className="w-100">
      <Title level={4}>{t("Pricing & Availability")}</Title>

      <CollapseCustom
        expandIconPosition="end"
        className={styles.pricingCollapse}
        destroyInactivePanel
        defaultActiveKey={[project?.pricingAndAvailability?.[0]?.unitType + 0]} // adding 0 in order to match the key as we are adding index in panelCustom
      >
        {project?.pricingAndAvailability?.map((section, index) => (
          <CollapsePanelCustom key={section?.unitType + index} header={renderHeader(section)}>
            <DataTable
              columns={pricingColumns(section)}
              data={tableData(section) || []}
              pagination={false}
              stickyOffset={0}
              size="small"
            />
          </CollapsePanelCustom>
        ))}
      </CollapseCustom>
    </Flex>
  )
}

export default PricingSection
